import React, { useCallback } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Image from "@tiptap/extension-image";
import Link from "@tiptap/extension-link";
import {
  Bold, Italic, List, ListOrdered, Heading2, Heading3, Link2, Image as ImageIcon, Undo, Redo, Quote,
} from "lucide-react";
import { api } from "@/lib/apiClient";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const Btn = ({ active, onClick, title, children, testId }) => (
  <button type="button" title={title} data-testid={testId} onClick={onClick}
    className={cn("w-8 h-8 rounded-lg flex items-center justify-center transition-colors",
      active ? "bg-teal text-white" : "text-muted-foreground hover:bg-secondary")}>
    {children}
  </button>
);

export default function RichTextEditor({ value, onChange, testId = "rich-editor" }) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [2, 3] } }),
      Image.configure({ HTMLAttributes: { class: "rounded-xl border border-border my-3 max-w-full" } }),
      Link.configure({ openOnClick: false, HTMLAttributes: { class: "text-teal underline" } }),
    ],
    content: value || "",
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
    editorProps: { attributes: { class: "prose prose-sm max-w-none min-h-[160px] p-3 focus:outline-none", "data-testid": `${testId}-area` } },
  }, []);

  const addImage = useCallback(async (e) => {
    const file = e.target.files?.[0]; if (!file || !editor) return;
    try {
      const fd = new FormData(); fd.append("file", file); fd.append("access", "public");
      const { data } = await api.post("/files/upload", fd);
      const url = data.url.startsWith("http") ? data.url : `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
      editor.chain().focus().setImage({ src: url }).run();
    } catch { toast.error("Image upload failed"); }
    e.target.value = "";
  }, [editor]);

  const setLink = useCallback(() => {
    if (!editor) return;
    const prev = editor.getAttributes("link").href;
    const url = window.prompt("Link URL", prev || "https://");
    if (url === null) return;
    if (url === "") { editor.chain().focus().unsetLink().run(); return; }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  }, [editor]);

  if (!editor) return null;
  return (
    <div className="border border-border rounded-xl overflow-hidden bg-white" data-testid={testId}>
      <div className="flex flex-wrap items-center gap-0.5 p-1.5 border-b border-border bg-secondary/40">
        <Btn title="Bold" active={editor.isActive("bold")} onClick={() => editor.chain().focus().toggleBold().run()} testId={`${testId}-bold`}><Bold className="w-4 h-4" /></Btn>
        <Btn title="Italic" active={editor.isActive("italic")} onClick={() => editor.chain().focus().toggleItalic().run()} testId={`${testId}-italic`}><Italic className="w-4 h-4" /></Btn>
        <span className="w-px h-5 bg-border mx-1" />
        <Btn title="Heading" active={editor.isActive("heading", { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} testId={`${testId}-h2`}><Heading2 className="w-4 h-4" /></Btn>
        <Btn title="Subheading" active={editor.isActive("heading", { level: 3 })} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}><Heading3 className="w-4 h-4" /></Btn>
        <Btn title="Bullet list" active={editor.isActive("bulletList")} onClick={() => editor.chain().focus().toggleBulletList().run()} testId={`${testId}-ul`}><List className="w-4 h-4" /></Btn>
        <Btn title="Numbered list" active={editor.isActive("orderedList")} onClick={() => editor.chain().focus().toggleOrderedList().run()}><ListOrdered className="w-4 h-4" /></Btn>
        <Btn title="Quote" active={editor.isActive("blockquote")} onClick={() => editor.chain().focus().toggleBlockquote().run()}><Quote className="w-4 h-4" /></Btn>
        <span className="w-px h-5 bg-border mx-1" />
        <Btn title="Link" active={editor.isActive("link")} onClick={setLink} testId={`${testId}-link`}><Link2 className="w-4 h-4" /></Btn>
        <label className="w-8 h-8 rounded-lg flex items-center justify-center text-muted-foreground hover:bg-secondary cursor-pointer" title="Insert image" data-testid={`${testId}-image`}>
          <ImageIcon className="w-4 h-4" />
          <input type="file" accept="image/*" className="hidden" onChange={addImage} />
        </label>
        <span className="w-px h-5 bg-border mx-1" />
        <Btn title="Undo" onClick={() => editor.chain().focus().undo().run()}><Undo className="w-4 h-4" /></Btn>
        <Btn title="Redo" onClick={() => editor.chain().focus().redo().run()}><Redo className="w-4 h-4" /></Btn>
      </div>
      <EditorContent editor={editor} />
    </div>
  );
}
